import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Evaluated360User } from 'src/app/360-evaluation/_interfaces/360-evaluation-user.interface';
import { ProcessConfig } from 'src/app/shared/interfaces/process-config.interface';
import { User } from '@sentry/browser';
import { ChangeRelationshipModal } from 'src/app/shared/modals/change-relationship/change-relationship.modal';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { AlertController, ModalController, ToastController } from '@ionic/angular';
import { Evaluation360Service } from 'src/app/360-evaluation/360-evaluation.service';
import { BaseClass } from 'src/app/shared/classes/base';
import { AreaConfig } from 'src/app/360-evaluation/_interfaces/area-config.interface';
import { ThreeSixtyEvaluationStatus } from 'src/app/360-evaluation/_interfaces/360-evaluation-status';
import { ConfigService } from 'src/app/shared/services/multi-clients/config.service';

@Component({
    selector: 'app-evaluation-users-list',
    templateUrl: './evaluation-users-list.component.html',
    styleUrls: ['../../360-evaluation.page.scss']
})
export class EvaluationUsersListComponent extends BaseClass {
    @Input() users: Array<Evaluated360User> = [];
    @Input() managerEvals: Array<Evaluated360User> = [];
    @Input() processConfig: ProcessConfig;
    @Input() cycleId: number = null;
    @Input() area?: AreaConfig = null;
    @Input() evaluatorCycleParticipantId: number = null;
    @Input() haveAccessToChangeRelationship: boolean = false;
    @Input() haveAccessToDelete: boolean = false;
    @Input() isAllowedToReopen: boolean = false;
    @Output() loadUsersFor360Evaluation: EventEmitter<boolean> = new EventEmitter();
    @Output() goToEvaluation: EventEmitter<Evaluated360User> = new EventEmitter();

    public disableRelationColumn: boolean;
    public enableEmptyStateMessageByArea: boolean;
    public hideAttributesFromUserSearchOn360: boolean;

    public readonly ThreeSixtyEvaluationStatus = ThreeSixtyEvaluationStatus;

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _modalController: ModalController,
        private _evaluationService: Evaluation360Service,
        private alertController: AlertController,
        private _configService: ConfigService
    ) {
        super(_toastController, _translationService);
        this.disableRelationColumn = this._configService.settings.features.disableRelationColumn;
        this.hideAttributesFromUserSearchOn360 = this._configService.settings.features.hideAttributesFromUserSearchOn360;
        this.enableEmptyStateMessageByArea = this._configService.settings.features.enableEmptyStateMessageByArea;
    }

    async ionViewWillEnter() {
    }

    public async confirmRelationshipChange(user: User) {
        let relationship = user.isManagerEvaluation ? '360' : this._translationService.getTranslation('generic.manager');
        let modalConfirmation = await this._modalController.create({
            component: ChangeRelationshipModal,
            cssClass: 'smaller-modal',
            componentProps: {
                'relation': relationship,
                'userEvaluated': user,
                'cycleId': this.cycleId,
                'evaluatorCycleParticipantId': this.evaluatorCycleParticipantId,
            }
        });
        await modalConfirmation.present();

        await modalConfirmation.onDidDismiss().then(async (response) => {
            if (response.data) {
                await this.loadUsersFor360Evaluation.emit();
            }
        });
    }

    public reopenEvaluation(user: Evaluated360User): void {
        this._evaluationService.reopenEvaluation(
            this.cycleId, user.userId
        ).subscribe((response) => {
            this._emitLoadUsersFor360Evaluation().then(() => {
                this.notifySuccess('performanceCycles.processReopened');
            });
        }, (error) => this.notifyError(error));
    }

    public async confirmDeleteEvaluation(evaluation: Evaluated360User): Promise<void> {
        const alert = await this.alertController.create({
            message: this._translationService.getTranslation('freeEvaluationMidYear.confirmDeleteEvaluation'),
            buttons: [{
                text: this._translationService.getTranslation('generic.no'),
                role: 'cancel',
                cssClass: 'danger'
            }, {
                text: this._translationService.getTranslation('generic.yes'),
                handler: () => {
                    this._deleteEvaluation(evaluation.userId);
                }
            }]
        });
        await alert.present();
    }

    public getNoEvaluationAreaMessage(): string {
        if (!this.area || !this.enableEmptyStateMessageByArea)
            return this._translationService.getTranslation('360evaluation.noEvaluations');
        return this._translationService.getTranslation('360evaluation.noEvaluationsArea' +
                this.area.globalizationConfig['en-US'].title.split(' ')[0]);
    }

    private _emitLoadUsersFor360Evaluation(): Promise<void> {
        return new Promise<void>((resolve) => {
            this.loadUsersFor360Evaluation.emit();
            resolve();
        });
    }

    private _deleteEvaluation(userId: string): void {
        this._evaluationService.deleteEvaluation(this.cycleId, userId)
            .subscribe(() => {
                this._emitLoadUsersFor360Evaluation().then(() => {
                    this.notifySuccess('360evaluation.evaluationDeleted');
                });
            }, (error) => this.notifyError(error));
    }

}
